import * as THREE from 'three';

/**
 * Creates a low-poly, angular "rock" geometry — flat-shaded facets,
 * irregular silhouette, no roundness, no cracks between faces.
 *
 * @param {number} radius - base size
 * @param {number} detail - icosahedron subdivision level (0 = 20 faces/very angular,
 *                           1 = 80 faces/moderately angular, 2 = smoother but still polygonal)
 * @param {number} deformation - how much each vertex is pushed in/out along its normal (0-1)
 * @param {number} seed - deterministic seed so the same seed = same rock every time
 */
export function createAsteroidGeometry({
  radius = 0.05,
  detail = 0,
  deformation = 0.45,
  seed = Math.random(),
} = {}) {
  // Indexed icosahedron: each corner vertex exists exactly ONCE here,
  // even though multiple triangles reference it. This is the key —
  // displacing now guarantees every triangle sharing a corner sees the
  // same displaced position, so the faces stay watertight.
  const geometry = new THREE.IcosahedronGeometry(radius, detail);

  const position = geometry.attributes.position;
  const vertexCount = position.count;

  // Deterministic pseudo-random generator
  let randomSeed = seed;
  const random = () => {
    randomSeed = (randomSeed * 9301 + 49297) % 233280;
    return randomSeed / 233280;
  };

  // One random displacement per UNIQUE vertex — not per face-corner.
  const vertex = new THREE.Vector3();
  for (let i = 0; i < vertexCount; i++) {
    vertex.fromBufferAttribute(position, i);
    const variation = 1 + (random() * 2 - 1) * deformation;
    vertex.multiplyScalar(variation);
    position.setXYZ(i, vertex.x, vertex.y, vertex.z);
  }
  position.needsUpdate = true;
  geometry.computeVertexNormals(); // update normals on the still-indexed geometry too

  // ONLY NOW split into non-indexed triangles for flat shading. Every
  // shared corner already has one consistent displaced position, so this
  // duplicates matching coordinates across adjacent faces — not diverging
  // ones — and the mesh stays sealed.
  const flatGeometry = geometry.index
    ? geometry.toNonIndexed()
    : geometry.clone();

  flatGeometry.computeVertexNormals();// recompute for true flat (per-face) normals

  return flatGeometry;
}